import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

const KycVerificationWidget = ({ userProfile, onVerificationStatusChanged }) => {
    const { showSuccess, showError } = useToast();
    
    const [panNumber, setPanNumber] = useState('');
    const [isVerifyingAadhaar, setIsVerifyingAadhaar] = useState(false);
    const [isVerifyingPan, setIsVerifyingPan] = useState(false);
    const [kycData, setKycData] = useState(null);
    const [aadhaarFile, setAadhaarFile] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [isLoadingKyc, setIsLoadingKyc] = useState(false);
    const [hasLoadedKyc, setHasLoadedKyc] = useState(false);
    // OTP-based Aadhaar is deprecated. Using document upload flow instead.

    const loadKycData = useCallback(async () => {
        // Prevent duplicate API calls
        if (hasLoadedKyc || isLoadingKyc) {
            console.log('[KycWidget] ⚠️ KYC data already loaded or loading, skipping duplicate call');
            return;
        }

        try {
            console.log('[KycWidget] 🔄 Loading KYC data...');
            setIsLoadingKyc(true);
            setHasLoadedKyc(true);
            
            const response = await apiService.fetchCandidateKYCDetails();
            
            if (response.success) {
                const data = response.data;
                
                // Map API response to internal structure and determine verification status
                const kycData = {
                    aadhaarNumber: data.aadhaarNumber || '',
                    aadhaarVerified: !!(data.aadhaarNumber && data.aadhaarNumber.trim()),
                    nameOnAadhaar: data.nameOnAadhaar || null,
                    panNumber: data.panNumber || '',
                    panVerified: !!(data.panNumber && data.panNumber.trim()),
                    // Prefer nameOnPAN, but support apiService's nameOnPan mapping
                    nameOnPAN: data.nameOnPAN || data.nameOnPan || null,
                    nameOnPan: data.nameOnPan || data.nameOnPAN || null, // Keep both for compatibility
                    phoneNumber: apiService.getMobileNumber()
                };
                
                console.log('[KycWidget] 📊 Raw API data:', data);
                console.log('[KycWidget] 📊 Mapped KYC data:', kycData);
                console.log('[KycWidget] 📊 PAN Name from API:', data.nameOnPAN);
                
                setKycData(kycData);
                
                // Pre-fill PAN input if available
                if (kycData.panNumber) {
                    setPanNumber(kycData.panNumber);
                }
                
                // Notify parent of current verification status
                if (onVerificationStatusChanged) {
                    console.log('[KycWidget] 📤 Notifying parent with KYC data:', {
                        aadhaarVerified: kycData.aadhaarVerified,
                        panVerified: kycData.panVerified,
                        nameOnPan: kycData.nameOnPan,
                        panNumber: kycData.panNumber
                    });
                    onVerificationStatusChanged(kycData.aadhaarVerified, kycData.panVerified, kycData);
                }
                
                console.log('[KycWidget] ✅ KYC data loaded successfully:', kycData);
                console.log('[KycWidget] 🔍 PAN Status Check:', {
                    panNumber: kycData.panNumber,
                    nameOnPan: kycData.nameOnPan,
                    panVerified: kycData.panVerified,
                    rawApiData: data
                });
            } else {
                console.log('[KycWidget] ❌ Failed to load KYC data:', response.error);
                // Initialize with empty data
                const emptyKycData = {
                    aadhaarNumber: '',
                    aadhaarVerified: false,
                    nameOnAadhaar: null,
                    panNumber: '',
                    panVerified: false,
                    nameOnPan: null,
                    nameOnPAN: null,
                    phoneNumber: apiService.getMobileNumber()
                };
                setKycData(emptyKycData);
            }
        } catch (error) {
            console.error('[KycWidget] 💥 Error loading KYC data:', error);
            showError('Failed to load KYC data');
        } finally {
            setIsLoadingKyc(false);
        }
    }, [showError, hasLoadedKyc, isLoadingKyc, onVerificationStatusChanged]);

    // Load KYC data on component mount - only once
    useEffect(() => {
        if (!hasLoadedKyc && !isLoadingKyc) {
            console.log('[KycWidget] 🚀 Initial KYC load triggered');
            loadKycData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array to ensure it only runs once

    // Notify parent component when KYC status changes - use useCallback to prevent infinite loops
    useEffect(() => {
        if (kycData && onVerificationStatusChanged) {
            const isAadhaarVerified = kycData.aadhaarVerified || false;
            const isPanVerified = kycData.panVerified || false;
            onVerificationStatusChanged(isAadhaarVerified, isPanVerified);
        }
    }, [kycData, onVerificationStatusChanged]);

    const validatePan = (number) => {
        return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(number.toUpperCase());
    };

    // No OTP validation needed for Aadhaar upload flow

    const onAadhaarFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        setAadhaarFile(file || null);
    };

    // Drag & Drop handlers for attractive upload UI
    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) {
            setAadhaarFile(file);
        }
        setIsDragActive(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = () => {
        setIsDragActive(false);
    };

    const uploadAndVerifyAadhaar = async () => {
        console.log('[KycWidget] uploadAndVerifyAadhaar called');
        console.log('[KycWidget] aadhaarFile:', aadhaarFile);
        
        if (!aadhaarFile) {
            console.log('[KycWidget] No file selected, showing error');
            showError('Please select your Aadhaar document (image/PDF).');
            return;
        }

        console.log('[KycWidget] Starting Aadhaar verification...');
        setIsVerifyingAadhaar(true);
        try {
            console.log('[KycWidget] Calling verifyCandidateAadhaar API with file:', {
                name: aadhaarFile.name,
                size: aadhaarFile.size,
                type: aadhaarFile.type
            });
            
            // Use the new verifyCandidateAadhaar API with legacy-services endpoint
            const response = await apiService.verifyCandidateAadhaar(aadhaarFile);
            
            console.log('[KycWidget] Candidate Aadhaar verification response:', response);
            
            // Check for error messages in the response data even if success is true
            const responseMessage = response.data?.message || response.message || '';
            const isActuallyFailed = responseMessage.toLowerCase().includes('not found') || 
                                   responseMessage.toLowerCase().includes('failed') ||
                                   responseMessage.toLowerCase().includes('error') ||
                                   responseMessage.toLowerCase().includes('invalid') ||
                                   responseMessage.toLowerCase().includes('unable') ||
                                   responseMessage.toLowerCase().includes('denied') ||
                                   response.status >= 400;
            
            console.log('[KycWidget] Response analysis:', {
                success: response.success,
                status: response.status,
                message: responseMessage,
                isActuallyFailed: isActuallyFailed
            });
            
            // Check if API response indicates success
            if (response.success && !isActuallyFailed) {
                const nameFromApi = response.data?.nameOnAadhaar || response.data?.name || null;
                const cleanedAadhaar = response.data?.aadhaarNumber || '';
                const updatedKycData = {
                    ...kycData,
                    aadhaarNumber: cleanedAadhaar,
                    aadhaarVerified: true,
                    nameOnAadhaar: nameFromApi || kycData?.nameOnAadhaar || null
                };
                setKycData(updatedKycData);
                showSuccess('Aadhaar verification successful!');
                onVerificationStatusChanged(true, kycData?.panVerified || false, updatedKycData);
            } else {
                // Handle error cases - API returned success:false or error message
                const errorMessage = response.data?.message || response.message || response.error || 'Aadhaar verification failed. Please try again.';
                console.log('[KycWidget] Showing error:', errorMessage);
                showError(errorMessage);
            }
        } catch (error) {
            console.error('[KycWidget] Aadhaar PDF verification error:', error);
            showError('Aadhaar verification failed. Please try again.');
        } finally {
            setIsVerifyingAadhaar(false);
        }
    };

    const verifyPan = async () => {
        if (!validatePan(panNumber)) {
            showError('Please enter a valid 10-character PAN number');
            return;
        }

        // Check if Aadhaar is verified first
        if (!kycData?.aadhaarVerified) {
            showError('Please verify your Aadhaar first before proceeding with PAN verification');
            return;
        }

        setIsVerifyingPan(true);
        
        try {
            // Verify PAN using real API (PAN verification doesn't require OTP)
            const response = await apiService.verifyPan(
                panNumber.toUpperCase(),
                kycData?.nameOnAadhaar || null,
                kycData?.aadhaarNumber || null
            );
            
            if (response.success) {
                // Update KYC data
                const updatedKycData = {
                    ...kycData,
                    panNumber: panNumber.toUpperCase(),
                    panVerified: true,
                    nameOnPan: response.data?.nameOnPan || null
                };
                setKycData(updatedKycData);
                
                showSuccess('PAN verification successful!');
                onVerificationStatusChanged(kycData?.aadhaarVerified || false, true, updatedKycData);
            } else {
                showError(response.error || 'PAN verification failed. Please try again.');
            }
        } catch (error) {
            console.error('[KycWidget] PAN verification error:', error);
            showError('PAN verification failed. Please try again.');
        } finally {
            setIsVerifyingPan(false);
        }
    };

    // OTP verification removed for Aadhaar flow

    // const formatAadhaar = (value) => {
    //     const cleaned = value.replace(/\D/g, '');
    //     const limited = cleaned.substring(0, 12);
    //     return limited.replace(/(\d{4})(?=\d)/g, '$1 ');
    // };

    const formatPan = (value) => {
        return value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
    };

    // Show loading state while fetching KYC data
    if (isLoadingKyc) {
        return (
            <div style={{
                background: 'white',
                borderRadius: 12,
                padding: '1.5rem',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                textAlign: 'center'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                }}>
                    <div style={{
                        width: 20,
                        height: 20,
                        border: '2px solid #e5e7eb',
                        borderTop: '2px solid #2563eb',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{ color: '#6b7280' }}>Loading KYC data...</span>
                </div>
            </div>
        );
    }

    // Check if KYC is already completed (both Aadhaar and PAN verified)
    const isKycCompleted = kycData?.aadhaarVerified && kycData?.panVerified;

    // Show completion status if KYC is already done
    if (isKycCompleted) {
        return (
            <div style={{
                background: 'white',
                borderRadius: 12,
                padding: '1.5rem',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '1.5rem'
                }}>
                    <i className="fas fa-shield-check" style={{
                        fontSize: '1.25rem',
                        color: '#16a34a'
                    }}></i>
                    <h3 style={{
                        margin: 0,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: '#6b7280'
                    }}>
                        KYC Verification Complete
                    </h3>
                </div>

                <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 8,
                    padding: '1rem',
                    marginBottom: '1rem'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem'
                    }}>
                        <i className="fas fa-check-circle" style={{
                            color: '#16a34a',
                            fontSize: '1rem'
                        }}></i>
                        <span style={{
                            fontWeight: 600,
                            color: '#16a34a'
                        }}>
                            Your KYC verification is complete!
                        </span>
                    </div>
                    <p style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: '#15803d',
                        lineHeight: '1.4'
                    }}>
                        Both your Aadhaar and PAN documents have been successfully verified. 
                        You can now access all features including earnings withdrawal.
                    </p>
                </div>

                {/* Show verified document details */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem'
                }}>
                    {/* Aadhaar Status */}
                    <div style={{
                        padding: '0.75rem',
                        background: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.5rem'
                        }}>
                            <i className="fas fa-id-card" style={{
                                color: '#2563eb',
                                fontSize: '0.875rem'
                            }}></i>
                            <span style={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#1f2937'
                            }}>
                                Aadhaar
                            </span>
                            <i className="fas fa-check-circle" style={{
                                color: '#16a34a',
                                fontSize: '0.75rem'
                            }}></i>
                        </div>
                        {kycData?.nameOnAadhaar && (
                            <p style={{
                                margin: 0,
                                fontSize: '0.75rem',
                                color: '#6b7280'
                            }}>
                                {kycData.nameOnAadhaar}
                            </p>
                        )}
                        {kycData?.aadhaarNumber && (
                            <p style={{
                                margin: '0.25rem 0 0 0',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontFamily: 'monospace'
                            }}>
                                {kycData.aadhaarNumber.replace(/(\d{4})(\d{4})(\d{4})/, 'xxxx xxxx $3')}
                            </p>
                        )}
                    </div>

                    {/* PAN Status */}
                    <div style={{
                        padding: '0.75rem',
                        background: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.5rem'
                        }}>
                            <i className="fas fa-credit-card" style={{
                                color: '#2563eb',
                                fontSize: '0.875rem'
                            }}></i>
                            <span style={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#1f2937'
                            }}>
                                PAN
                            </span>
                            <i className="fas fa-check-circle" style={{
                                color: '#16a34a',
                                fontSize: '0.75rem'
                            }}></i>
                        </div>
                        {(kycData?.nameOnPAN || kycData?.nameOnPan) && (
                            <p style={{
                                margin: 0,
                                fontSize: '0.75rem',
                                color: '#6b7280'
                            }}>
                                {kycData.nameOnPAN || kycData.nameOnPan}
                            </p>
                        )}
                        {kycData?.panNumber && (
                            <p style={{
                                margin: '0.25rem 0 0 0',
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontFamily: 'monospace'
                            }}>
                                {kycData.panNumber.replace(/^.{6}/, 'XXXXXX')}
                            </p>
                        )}
                    </div>
                </div>

            </div>
        );
    }

    return (
        <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '1.5rem',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>

            {/* Aadhaar Verification Section */}
            <div style={{
                marginBottom: '2rem',
                padding: '1rem',
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e5e7eb'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1rem'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <i className="fas fa-id-card" style={{
                            color: '#2563eb',
                            fontSize: '1rem'
                        }}></i>
                        <span style={{
                            fontWeight: 600,
                            color: '#1f2937'
                        }}>
                            Aadhaar Verification
                        </span>
                    </div>
                    {kycData?.aadhaarVerified && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            background: '#dcfce7',
                            padding: '0.25rem 0.5rem',
                            borderRadius: 12,
                            border: '1px solid #bbf7d0'
                        }}>
                            <i className="fas fa-check-circle" style={{
                                fontSize: '0.75rem',
                                color: '#16a34a'
                            }}></i>
                            <span style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#16a34a'
                            }}>
                                Verified
                            </span>
                        </div>
                    )}
                </div>

                {/* Show Aadhaar details if verified */}
                {kycData?.aadhaarVerified && (kycData?.nameOnAadhaar || kycData?.aadhaarNumber) && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        background: '#f0f9ff',
                        borderRadius: 8,
                        border: '1px solid #bfdbfe'
                    }}>
                        {kycData?.nameOnAadhaar && (
                            <div style={{
                                marginBottom: kycData?.aadhaarNumber ? '0.5rem' : '0'
                            }}>
                                <span style={{
                                    fontSize: '0.75rem',
                                    color: '#1e40af',
                                    fontWeight: 600
                                }}>
                                    Name: {kycData.nameOnAadhaar}
                                </span>
                            </div>
                        )}
                        {kycData?.aadhaarNumber && (
                            <div>
                                <span style={{
                                    fontSize: '0.75rem',
                                    color: '#1e40af',
                                    fontWeight: 600,
                                    fontFamily: 'monospace'
                                }}>
                                    Aadhaar No.: {kycData.aadhaarNumber.replace(/(\d{4})(\d{4})(\d{4})/, 'xxxx xxxx $3')}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {!kycData?.aadhaarVerified && (
                    <div>
                        {/* Hidden native input for accessibility */}
                        <input
                            id="aadhaarUpload"
                            type="file"
                            accept="application/pdf"
                            onChange={onAadhaarFileChange}
                            style={{ display: 'none' }}
                        />

                        {/* Dropzone styled card */}
                        <div
                            role="button"
                            tabIndex={0}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    // Prevent bubbling to parent handlers that may also trigger .click()
                                    e.preventDefault();
                                    e.stopPropagation();
                                    document.getElementById('aadhaarUpload')?.click();
                                }
                            }}
                            onClick={() => document.getElementById('aadhaarUpload')?.click()}
                            style={{
                                border: `2px dashed ${isDragActive ? '#3b82f6' : '#cbd5e1'}`,
                                background: isDragActive ? '#eff6ff' : 'white',
                                borderRadius: 12,
                                padding: '1.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                transition: 'all 150ms ease-in-out',
                                cursor: 'pointer',
                                marginBottom: '0.75rem'
                            }}
                        >
                            <div style={{
                                width: 52,
                                height: 52,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                boxShadow: '0 6px 20px rgba(37,99,235,0.25)'
                            }}>
                                <i className="fas fa-cloud-upload-alt" style={{ color: 'white', fontSize: 20 }}></i>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: 600, color: '#1f2937' }}>
                                    {aadhaarFile ? 'File selected' : 'Drag & drop your Aadhaar PDF here'}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                    or
                                    <span style={{ marginLeft: 6, marginRight: 6 }}></span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        // Stop event from bubbling to dropzone onClick
                                        e.stopPropagation();
                                        document.getElementById('aadhaarUpload')?.click();
                                    }}
                                    style={{
                                        display: 'inline-block',
                                        padding: '0.45rem 0.75rem',
                                        borderRadius: 8,
                                        background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                        color: 'white',
                                        fontWeight: 600,
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.25)',
                                        cursor: 'pointer',
                                        border: 'none'
                                    }}
                                    aria-label="Choose Aadhaar file"
                                >
                                    Choose file
                                </button>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 6 }}>
                                    Accepted: PDF only
                                </div>
                            </div>
                            {aadhaarFile && (
                                <div style={{
                                    marginTop: '0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#065f46',
                                    background: '#ecfdf5',
                                    border: '1px solid #a7f3d0',
                                    borderRadius: 8,
                                    padding: '0.4rem 0.6rem'
                                }}>
                                    <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                                    <span style={{ fontSize: '0.8rem' }}>{aadhaarFile.name}</span>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={uploadAndVerifyAadhaar}
                            disabled={isVerifyingAadhaar || !aadhaarFile}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: isVerifyingAadhaar || !aadhaarFile ? '#9ca3af' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                                cursor: isVerifyingAadhaar || !aadhaarFile ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                boxShadow: isVerifyingAadhaar || !aadhaarFile ? 'none' : '0 6px 20px rgba(37,99,235,0.25)'
                            }}
                        >
                            {isVerifyingAadhaar && (
                                <div style={{
                                    width: 16,
                                    height: 16,
                                    border: '2px solid transparent',
                                    borderTop: '2px solid white',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                            )}
                            {isVerifyingAadhaar ? 'Verifying...' : 'Upload & Verify Aadhaar'}
                        </button>
                    </div>
                )}
            </div>

            {/* PAN Verification Section */}
            <div style={{
                padding: '1rem',
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e5e7eb'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1rem'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <i className="fas fa-credit-card" style={{
                            color: '#2563eb',
                            fontSize: '1rem'
                        }}></i>
                        <span style={{
                            fontWeight: 600,
                            color: '#1f2937'
                        }}>
                            PAN Verification
                        </span>
                    </div>
                    {kycData?.panVerified && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            background: '#dcfce7',
                            padding: '0.25rem 0.5rem',
                            borderRadius: 12,
                            border: '1px solid #bbf7d0'
                        }}>
                            <i className="fas fa-check-circle" style={{
                                fontSize: '0.75rem',
                                color: '#16a34a'
                            }}></i>
                            <span style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#16a34a'
                            }}>
                                Verified
                            </span>
                        </div>
                    )}
                </div>

                {/* Show PAN details if verified */}
                {kycData?.panVerified && ((kycData?.nameOnPAN || kycData?.nameOnPan) || kycData?.panNumber) && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        background: '#f0f9ff',
                        borderRadius: 8,
                        border: '1px solid #bfdbfe'
                    }}>
                        {(kycData?.nameOnPAN || kycData?.nameOnPan) && (
                            <div style={{
                                marginBottom: kycData?.panNumber ? '0.5rem' : '0'
                            }}>
                                <span style={{
                                    fontSize: '0.75rem',
                                    color: '#1e40af',
                                    fontWeight: 600
                                }}>
                                    Name: {kycData.nameOnPAN || kycData.nameOnPan}
                                </span>
                            </div>
                        )}
                        {kycData?.panNumber && (
                            <div>
                                <span style={{
                                    fontSize: '0.75rem',
                                    color: '#1e40af',
                                    fontWeight: 600,
                                    fontFamily: 'monospace'
                                }}>
                                    PAN No.: {kycData.panNumber.replace(/^.{6}/, 'XXXXXX')}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {!kycData?.panVerified && (
                    <div>
                        <input
                            type="text"
                            placeholder="Enter 10-character PAN (e.g., ABCDE1234F)"
                            value={panNumber}
                            onChange={(e) => setPanNumber(formatPan(e.target.value))}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: 8,
                                fontSize: '0.875rem',
                                marginBottom: '1rem',
                                fontFamily: 'monospace',
                                textTransform: 'uppercase'
                            }}
                        />
                        <button
                            onClick={verifyPan}
                            disabled={isVerifyingPan}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                background: isVerifyingPan ? '#9ca3af' : '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                                cursor: isVerifyingPan ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            {isVerifyingPan && (
                                <div style={{
                                    width: 16,
                                    height: 16,
                                    border: '2px solid transparent',
                                    borderTop: '2px solid white',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                            )}
                            {isVerifyingPan ? 'Verifying...' : 'Verify PAN'}
                        </button>
                    </div>
                )}
            </div>

            {/* Information Section */}
            <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#eff6ff',
                borderRadius: 8,
                border: '1px solid #bfdbfe'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                }}>
                    <i className="fas fa-info-circle" style={{
                        color: '#2563eb',
                        fontSize: '0.875rem'
                    }}></i>
                    <span style={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#1e40af'
                    }}>
                        Important Information
                    </span>
                </div>
                <ul style={{
                    margin: 0,
                    paddingLeft: '1.25rem',
                    fontSize: '0.75rem',
                    color: '#374151',
                    lineHeight: 1.5
                }}>
                    <li>KYC verification is mandatory for earnings withdrawal</li>
                    <li>Upload a clear PDF of your Aadhaar for verification</li>
                    <li>Ensure your PAN details are accurate</li>
                </ul>
            </div>

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default KycVerificationWidget;
